/**
 * The pages a Product may enable — the closed set the six Declared_Pages name
 * (R2.1, R2.4).
 *
 * A Declared_Page renders from composed capability components against a Product's
 * declaration, so this set is closed: a page a single Product needs that no
 * Declared_Page expresses is a Bespoke_Page (code), not a seventh key.
 */
export type DeclaredPageKey =
  | 'overview'
  | 'financials'
  | 'support'
  | 'users'
  | 'disputes'
  | 'email-templates';

/** The closed set of Declared_Page keys, as a runtime tuple. */
export const DECLARED_PAGE_KEYS = [
  'overview',
  'financials',
  'support',
  'users',
  'disputes',
  'email-templates',
] as const satisfies ReadonlyArray<DeclaredPageKey>;

/**
 * A Standing_Field a Product declares beyond the common standing set, handed to the
 * Support_Capability's Account_Overview (R8.2).
 *
 * The key uses the platform's declared-key character rule; the label is for a reader.
 */
export interface StandingFieldDeclaration {
  readonly key: string;
  readonly label: string;
}

/**
 * A Bespoke_Page a Product declares: a page that Product alone needs, which no
 * Declared_Page expresses, mounted as a code module for that Product (R2.5).
 *
 * The declaration names it; the code that renders it is registered with the page
 * framework under {@link key}, so a Declared_Page depends on none of them and the
 * framework mounts it for this Product's routes alone. A Bespoke_Page naming an
 * unknown Product, or a `key` no registered module answers, is a validation fault.
 */
export interface BespokePageDeclaration {
  /** The key the page framework resolves the rendering module by. */
  readonly key: string;

  /** The path segment under the Product, and the navigation label. */
  readonly path: string;
  readonly label: string;
}

/**
 * What the Admin_Panel is configured with for one Product at deploy time (R2.1).
 *
 * A Product is a declaration, not an application: a new Product is a new entry here
 * plus its configuration, not a code change — the one exception being a Bespoke_Page,
 * which is code (R1.2, R2.3, R2.5). The enabled-page set is discriminated so a page is
 * reachable only through the configuration it needs: financials and disputes only
 * through a `billing` block, overview traffic only through an `analytics` block, so a
 * page enabled with no configuration is a type error before it is a validation one.
 */
export interface ProductDeclaration {
  /** The Product's stable key, by the platform's declared-key character rule (R2.2). */
  readonly key: string;

  /** The Product's human-readable label. */
  readonly label: string;

  /** Enables the Overview traffic; the Google Analytics property is the credential's. */
  readonly analytics?: { readonly property: string };

  /** Enables the Financials and Disputes pages. */
  readonly billing?: {
    readonly processor: string;
    readonly configuration: unknown;
  };

  /** The mail domains and organisations this Product owns, handed to Support. */
  readonly mail: {
    readonly domains: ReadonlyArray<string>;
    readonly organisations: ReadonlyArray<string>;
  };

  /** The Subject_Kinds handed to the Support_Capability. */
  readonly subjectKinds: ReadonlyArray<string>;

  /** The Standing_Fields beyond the common set, handed to the Support_Capability. */
  readonly standingFields: ReadonlyArray<StandingFieldDeclaration>;

  /** The Declared_Pages this Product enables — a subset of the closed six. */
  readonly enabledPages: ReadonlyArray<DeclaredPageKey>;

  /** The Bespoke_Pages this Product declares, each a code module (R2.5). */
  readonly bespokePages?: ReadonlyArray<BespokePageDeclaration>;
}

/**
 * The org-wide set of Product_Declarations the Admin_Panel is configured with (R1.2).
 */
export type ProductsDeclaration = ReadonlyArray<ProductDeclaration>;
